import React, { useEffect } from 'react';
import { Card } from '@startuptools/ui/Card';
import { Typography } from '@startuptools/ui/Typography';
import { Button } from '@startuptools/ui/Button';
import { Stack } from '@startuptools/ui/Stack';
import { Box } from '@startuptools/ui/Box';
import { Input } from '@startuptools/ui/Input';
import { CircularProgress } from '@startuptools/ui/CircularProgress';
import { Alert } from '@startuptools/ui/Alert';
import { Skeleton } from '@startuptools/ui/Skeleton';
import { Trans, useLingui } from '@lingui/react';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import { DocumentPreview } from './components/document-preview';
import SearchIcon from '@mui/icons-material/Search';
import { GqlDocSystemTagKind, GqlDocumentTemplate } from '../../../graphql/react-operations';
import { ConfirmDialog } from '@startuptools/ui/ConfirmDialog';
import { IconButton } from '@startuptools/ui/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { CreateAgreement } from './create-agreement';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import LaunchIcon from '@mui/icons-material/Launch';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { Action, Column, ItemList } from '@startuptools/ui/ItemList';
import { MAX_FILE_SIZE, useFileDrop } from '@startuptools/ui/FileUploadInput';
import { allAccept, DocumentCreateDialog } from '@startuptools/business-ui/document/DocumentCreateDialog';
import { SelfLoadingDocumentDialog } from '../../../components/document-dialog/DocumentDialog';
import { useDropzone } from 'react-dropzone';
import { apiClient } from '../../../common/http';
import { toast } from '@startuptools/ui/toast/use-toast';
import { AlertConfirmDialog } from '../../../components/simple-dialogs/AlertConfirmDialog';
import {
  FileViewDocument,
  GqlFileViewQuery,
  useFileSoftDeleteMutation,
  useMoveFileMutation,
} from './file-view.graphql';
import { DateFormat } from '@startuptools/ui/DateFormat';
import { DateTime } from 'luxon';
import { CompanyDocumentsSignatures } from './CompanyDocumentsSignatures';
import { DocumentSignatureStatus } from '@startuptools/business-ui/document/DocumentSignatureStatus';
import { useForm } from 'react-hook-form';
import { ErrorBoundary } from '@startuptools/ui/ErrorBoundary';
import { CreateFolder } from './create-folder';
import { DocKind } from '../../../free-templates/types';
import { isArray } from 'lodash-es';
import { MoveFileDialog } from './MoveFile';
import { useNavigate } from '../../../components/Navigate';
import { meetingLink } from '../../../components/Links';
import { NdaDialog } from './components/NdaDialog';

type ListItem = GqlFileViewQuery['file']['children']['models'][0];

type DialogType =
  | { type: 'create'; template: GqlDocumentTemplate }
  | { type: 'legacy'; docKind: DocKind }
  | { type: 'delete'; id: string }
  | {
      type: 'upload';
      file?: File | null | undefined;
      folder: Pick<GqlFileViewQuery['file'], 'id'>;
    }
  | { type: 'upgrade' }
  | { type: 'document'; id: string }
  | { type: 'createFolder' }
  | { type: 'move'; id: string }
  | null;

type DialogState = {
  shown: DialogType;
};

type DialogAction = { type: 'SHOW_DIALOG'; dialog: DialogType } | { type: 'HIDE_DIALOG' };

const dialogReducer = (state: DialogState, action: DialogAction): DialogState => {
  switch (action.type) {
    case 'SHOW_DIALOG':
      return { shown: action.dialog };
    case 'HIDE_DIALOG':
      return { shown: null };
    default:
      return state;
  }
};

const initialDialogState: DialogState = {
  shown: null,
};

const ItemCard = ({
  item,
  onFileDrop,
  locale,
}: {
  item: ListItem;
  onFileDrop: (file: File, folder: Pick<GqlFileViewQuery['file'], 'id'>) => unknown;
  locale: string;
}) => {
  const onDrop = useFileDrop(file => onFileDrop(file, item));
  const dropEnabled = item.kind === GqlDocSystemTagKind.Folder;
  const { getRootProps, isDragActive } = useDropzone({
    disabled: !dropEnabled,
    onDrop: (acceptedFiles, fileRejections, event) => {
      if (!isArray(event)) {
        event.preventDefault();
        event.stopPropagation();
      }
      onDrop(acceptedFiles, fileRejections);
    },
    accept: allAccept,
    maxFiles: 1,
    maxSize: MAX_FILE_SIZE,
  });

  return (
    <Box
      {...getRootProps()}
      sx={{
        bgcolor: isDragActive && dropEnabled ? 'background.level1' : 'background.surface',
        minHeight: 100,
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        border: isDragActive ? 2 : 0,
        borderRadius: 1,
        borderColor: isDragActive && dropEnabled ? 'primary.400' : 'neutral.outlinedBorder',
        '&:hover': {
          bgcolor: 'background.level1',
          borderColor: 'primary.500',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
        {item.kind === GqlDocSystemTagKind.Folder ? (
          <FolderOpenIcon sx={{ fontSize: 32, color: 'primary.500' }} />
        ) : (
          <AssignmentIcon sx={{ fontSize: 32, color: 'primary.500' }} />
        )}
        <Box sx={{ overflow: 'hidden' }}>
          <Typography
            sx={{
              fontSize: [14, 16],
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {(locale === 'en' ? item.filenameSecondaryLang : item.filename) ?? item.filename}
          </Typography>

          <Typography
            sx={{
              fontSize: [10, 12],
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            color="neutral"
          >
            {item.kind === GqlDocSystemTagKind.Folder && (
              <Trans id="{count} documents" values={{ count: item.children.totalCount }} />
            )}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

type Props = {
  folderId: string | null;
  showNewDocumentDialog: (kind: DocKind) => Promise<boolean>;
  data: GqlFileViewQuery;
  refetch: () => void;
  onSearch: (searchString: string) => void;
  searchString: string;
} & Pick<React.ComponentProps<typeof ItemList>, 'pagination'>;

export const Folder = ({ folderId, showNewDocumentDialog, data, refetch, onSearch, searchString, ...rest }: Props) => {
  const companyId = useCompanyId();
  const [pending, startTransition] = React.useTransition();
  const { i18n } = useLingui();
  const { link, navigate } = useNavigate();
  const typingRef = React.useRef<NodeJS.Timeout | null>(null);

  const { register, reset, handleSubmit, watch } = useForm<{ searchString: string }>({
    defaultValues: { searchString: '' },
    mode: 'onChange',
  });

  const formValues = watch();

  const onSubmit = React.useMemo(
    () =>
      handleSubmit(data => {
        startTransition(() => {
          typingRef.current = null;
          onSearch(data.searchString);
        });
      }),
    [handleSubmit, onSearch],
  );

  useEffect(() => {
    if (typingRef.current) clearTimeout(typingRef.current);
    if (formValues.searchString.length === 0 && searchString.length === 0) return;

    const timeout = setTimeout(() => {
      void onSubmit();
    }, 1000);

    typingRef.current = timeout;

    return () => {
      clearTimeout(typingRef.current!);
      typingRef.current = null;
    };
  }, [formValues.searchString, onSubmit, searchString.length]);

  const [dialogState, dispatch] = React.useReducer(dialogReducer, initialDialogState);

  const [moveFile, { loading: moveFileLoading }] = useMoveFileMutation({
    refetchQueries: [FileViewDocument],
  });

  const handleMoveFile = React.useCallback(
    async (sourceId: string, targetId: string | null) => {
      try {
        await moveFile({ variables: { input: { id: sourceId, targetId } } });
        toast({
          title: 'Success',
          description: <Trans id="Moved file into folder" />,
          severity: 'success',
        });
      } catch {
        toast({
          title: 'Error',
          description: <Trans id="Failed to move file" />,
          severity: 'danger',
        });
      }
    },
    [moveFile],
  );

  const handleUploadClick = React.useCallback(
    (file: File | null | undefined, folder: Pick<GqlFileViewQuery['file'], 'id'>) => {
      if (!data.companySubscription?.isActive && !data.company.isDemo) {
        return dispatch({ type: 'SHOW_DIALOG', dialog: { type: 'upgrade' } });
      }
      dispatch({
        type: 'SHOW_DIALOG',
        dialog: {
          type: 'upload',
          folder,
          file,
        },
      });
      return;
    },
    [data.companySubscription?.isActive, data.company.isDemo, dispatch],
  );

  const onFileDrop = useFileDrop(file => handleUploadClick(file, data.file));

  const { getRootProps, isDragActive, fileRejections } = useDropzone({
    onDrop: (files, rejectedFiles) => {
      onFileDrop(files, rejectedFiles);
    },
    accept: allAccept,
    maxFiles: 1,
    maxSize: MAX_FILE_SIZE,
  });
  const error = fileRejections.length > 0;

  const [doDelete, { loading }] = useFileSoftDeleteMutation({
    refetchQueries: [FileViewDocument],
  });

  const handleDelete = async () => {
    try {
      if (dialogState.shown?.type !== 'delete') return;
      await doDelete({ variables: { companyId, id: dialogState.shown.id } });
      dispatch({ type: 'HIDE_DIALOG' });
    } catch {
      toast({
        title: 'Error',
        description: <Trans id="Failed to delete document" />,
        severity: 'danger',
      });
    }
  };

  const columns: Column<ListItem>[] = [
    {
      header: '',
      width: '2em',
      render: item =>
        item.kind === GqlDocSystemTagKind.Folder ? (
          <FolderOpenIcon sx={{ fontSize: 24, color: 'primary.500' }} />
        ) : (
          <AssignmentIcon sx={{ fontSize: 24, color: 'primary.500' }} />
        ),
    },

    {
      header: <Trans id="Name" />,
      width: '35%',
      render: item => (i18n.locale === 'en' ? item.filenameSecondaryLang : item.filename) ?? item.filename,
    },
    {
      header: <Trans id="Count" />,
      render: item => (item.kind === GqlDocSystemTagKind.Folder ? item.children.totalCount : null),
    },
    {
      header: <Trans id="Created At" />,
      render: item => (
        <DateFormat locale={i18n.locale} format={DateTime.DATETIME_SHORT_WITH_SECONDS}>
          {item.kind !== GqlDocSystemTagKind.Folder ? item.createdAt : null}
        </DateFormat>
      ),
    },
    {
      header: <Trans id="Signatories" />,
      render: item =>
        item.documentSignatures && <CompanyDocumentsSignatures documentSignatures={item.documentSignatures} />,
    },
    {
      header: <Trans id="Signature Status" />,
      render: item =>
        item.scriveStatus || item.zignedAgreementStatus ? (
          <DocumentSignatureStatus
            scriveStatus={item.scriveStatus}
            zignedAgreementStatus={item.zignedAgreementStatus}
            signatureMethod={item.signatureMethod}
          />
        ) : null,
    },
  ];

  const actions = (item: ListItem) => {
    const actions: Action<ListItem>[] = [
      {
        icon: <DriveFileMoveIcon />,
        label: <Trans id="Move" />,
        onClick: () => {
          dispatch({ type: 'SHOW_DIALOG', dialog: { type: 'move', id: item.id } });
        },
      },
    ];
    if (item.meetingDocument?.meeting.id) {
      actions.push({
        icon: <LaunchIcon />,
        label: <Trans id="View" />,
        link: () => (item.meetingDocument?.meeting ? link(meetingLink(item.meetingDocument.meeting)) : undefined),
      });
    }
    return actions;
  };

  const templates = React.useMemo<
    (GqlDocumentTemplate | { id: DocKind; name: React.ReactNode; legacy: boolean })[]
  >(() => {
    return [
      ...data.documentTemplates,
      { id: DocKind.OneWayNda, name: <Trans id="One-Way NDA" />, legacy: true },
      { id: DocKind.TwoWayNda, name: <Trans id="Two-Way NDA" />, legacy: true },
      { id: DocKind.PermanentEmployment, name: <Trans id="Employment Agreement" />, legacy: true },
      { id: DocKind.ManagingDirector, name: <Trans id="Employment Agreement CEO" />, legacy: true },
    ];
  }, [data.documentTemplates]);

  return (
    <Stack
      {...getRootProps()}
      p={2}
      border={isDragActive ? 2 : 0}
      borderRadius={1}
      borderColor={isDragActive ? 'primary.400' : error ? 'warning.400' : 'neutral.outlinedBorder'}
      bgcolor={isDragActive ? 'background.level1' : 'white'}
      minHeight={100}
      gap={2}
    >
      <Card variant="solid" sx={{ backgroundColor: isDragActive ? 'background.level1' : 'white', padding: 0 }}>
        <Stack spacing={2}>
          {!folderId && (
            <Card>
              <Typography level="h4">
                <Trans id="Templates" />
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', px: 2, py: 2 }}>
                {templates.map(template => {
                  return (
                    <DocumentPreview
                      key={template.id}
                      name={template.name}
                      component="button"
                      onClick={() => {
                        if (!data.companySubscription?.isActive && !data.company.isDemo) {
                          return dispatch({ type: 'SHOW_DIALOG', dialog: { type: 'upgrade' } });
                        }

                        if ('legacy' in template) {
                          if (template.id === DocKind.OneWayNda || template.id === DocKind.TwoWayNda) {
                            return dispatch({ type: 'SHOW_DIALOG', dialog: { type: 'legacy', docKind: template.id } });
                          } else {
                            return showNewDocumentDialog(template.id);
                          }
                        }
                        return dispatch({ type: 'SHOW_DIALOG', dialog: { type: 'create', template } });
                      }}
                      sx={{
                        minWidth: 200,
                        p: 2,
                        '& button': {
                          visibility: 'hidden',
                          transition: 'all 0.15s ease',
                        },
                        '&:hover button': {
                          visibility: 'visible',
                        },
                        '&:hover .skeleton': {
                          display: 'none',
                        },
                        '&:hover': {
                          backgroundColor: 'background.level1',
                          borderColor: 'primary.500',
                          transform: 'scale(1.02)',
                        },
                      }}
                    >
                      <Skeleton className="skeleton" variant="text" level="body-sm" animation={false} />
                      <Skeleton className="skeleton" variant="text" level="body-sm" animation={false} />
                      <Skeleton className="skeleton" variant="text" level="body-sm" animation={false} />
                      <Button variant="outlined" size="sm">
                        <Trans id="Create" />
                      </Button>
                    </DocumentPreview>
                  );
                })}
              </Box>
            </Card>
          )}

          <Stack direction="row" gap={2}>
            <form onSubmit={onSubmit}>
              <Input
                fullWidth
                {...register('searchString')}
                startDecorator={typingRef.current || pending ? <CircularProgress size="sm" /> : <SearchIcon />}
                endDecorator={
                  searchString?.length ? (
                    <IconButton
                      onClick={() => {
                        reset();
                        onSearch('');
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  ) : undefined
                }
                type="search"
                placeholder={i18n._('Search...')}
              />
            </form>

            <Box>
              <Button onClick={() => handleUploadClick(null, data.file)}>
                <Trans id="Upload file" />
              </Button>
            </Box>

            <Box>
              <Button
                onClick={() => dispatch({ type: 'SHOW_DIALOG', dialog: { type: 'createFolder' } })}
                variant="outlined"
              >
                <Trans id="Create folder" />
              </Button>
            </Box>
          </Stack>

          <Box sx={{ pb: 2 }}>
            <ErrorBoundary
              fallback={({ resetError }) => (
                <Alert variant="outlined" color="danger">
                  <Trans id="Something went wrong, please try again and contact support if the problem persists" />
                  <Button color="neutral" variant="outlined" onClick={resetError}>
                    <Trans id="Reset Search" />
                  </Button>
                </Alert>
              )}
            >
              {data.file.children.models.length ? (
                <ItemList
                  {...rest}
                  actions={actions}
                  items={data.file.children.models.map(item => {
                    const deletable =
                      item.kind === GqlDocSystemTagKind.Folder ? item.children.totalCount === 0 : item.softDeletable;
                    return {
                      ...item,
                      deletable,
                      draggable: item.kind !== GqlDocSystemTagKind.Folder,
                      droppable: item.kind === GqlDocSystemTagKind.Folder,
                    };
                  })}
                  onItemClick={({ id, kind }) => {
                    if ([GqlDocSystemTagKind.Folder, GqlDocSystemTagKind.Agreement].includes(kind)) {
                      return navigate(`/documents/company-documents/${id}`);
                    }
                    dispatch({ type: 'SHOW_DIALOG', dialog: { type: 'document', id } });
                  }}
                  onDelete={({ id }) =>
                    dispatch({
                      type: 'SHOW_DIALOG',
                      dialog: { type: 'delete', id },
                    })
                  }
                  onDrop={handleMoveFile}
                  columns={columns}
                  renderCard={item => {
                    return <ItemCard locale={i18n.locale} item={item} onFileDrop={() => onFileDrop} />;
                  }}
                  defaultView="table"
                />
              ) : (
                <Card>
                  <Stack alignItems="center">
                    <Typography level="h4">
                      <Trans id="This folder is empty" />
                    </Typography>
                    <Typography level="body-sm" sx={{ textAlign: 'center', mt: 4, maxWidth: '50%' }}>
                      <Trans id="Upload your first file to get started. Documents generated on the platform will be categorized automatically." />
                    </Typography>
                  </Stack>
                </Card>
              )}
            </ErrorBoundary>
          </Box>

          {dialogState.shown?.type === 'delete' && (
            <AlertConfirmDialog
              content={<Trans id="Are you sure you want to permanently delete this file?" />}
              onConfirm={handleDelete}
              loading={loading}
              onClose={() => dispatch({ type: 'HIDE_DIALOG' })}
            />
          )}

          {dialogState.shown?.type === 'document' && (
            <SelfLoadingDocumentDialog
              documentId={dialogState.shown.id}
              onClose={() => dispatch({ type: 'HIDE_DIALOG' })}
            />
          )}

          {dialogState.shown?.type === 'create' && (
            <CreateAgreement close={() => dispatch({ type: 'HIDE_DIALOG' })} template={dialogState.shown.template} />
          )}
          {dialogState.shown?.type === 'legacy' && (
            <>
              {(dialogState.shown.docKind === DocKind.OneWayNda || dialogState.shown.docKind === DocKind.TwoWayNda) && (
                <NdaDialog
                  docKind={dialogState.shown.docKind}
                  onClose={() => dispatch({ type: 'HIDE_DIALOG' })}
                  onOpenDocument={id => dispatch({ type: 'SHOW_DIALOG', dialog: { type: 'document', id } })}
                />
              )}
            </>
          )}

          {dialogState.shown?.type === 'upload' && (
            <DocumentCreateDialog
              allowDigitalSignature
              showDigitalSignatureWarning
              mode="upload"
              file={dialogState.shown.file || undefined}
              onClose={() => dispatch({ type: 'HIDE_DIALOG' })}
              upload={async ({ file, signDigitally }) => {
                if (dialogState.shown?.type !== 'upload') throw Error('unexpected');
                try {
                  await apiClient.uploadFile(
                    file,
                    file.name,
                    companyId,
                    null,
                    signDigitally,
                    GqlDocSystemTagKind.Other,
                    dialogState.shown.folder.id,
                  );
                  toast({
                    title: 'Success',
                    description: <Trans id="Document uploaded" />,
                    severity: 'success',
                  });
                  dispatch({ type: 'HIDE_DIALOG' });
                  startTransition(() => {
                    void refetch();
                  });
                } catch (error) {
                  console.error(error);
                  toast({
                    title: 'Error',
                    description: <Trans id="Failed to upload document" />,
                    severity: 'danger',
                  });
                }
              }}
              showExistingDocWarning={false}
              showSigningWarning={false}
              acceptMimeTypes={allAccept}
            />
          )}

          {dialogState.shown?.type === 'createFolder' && (
            <CreateFolder close={() => dispatch({ type: 'HIDE_DIALOG' })} parentId={folderId} />
          )}

          {dialogState.shown?.type === 'move' && (
            <MoveFileDialog
              onClose={() => dispatch({ type: 'HIDE_DIALOG' })}
              fileId={dialogState.shown.id}
              currentFolderId={folderId}
              moveFile={handleMoveFile}
              loading={moveFileLoading}
            />
          )}

          {dialogState.shown?.type === 'upgrade' && (
            <ConfirmDialog
              header={<Trans id="To create a new document an active subscription is required" />}
              confirmProps={{ children: <Trans id="Upgrade" /> }}
              onCancel={() => dispatch({ type: 'HIDE_DIALOG' })}
              onConfirm={() => navigate(`/settings/billing`)}
            >
              <Trans id="Upgrade your subscription to gain access to document management including e-signing" />
            </ConfirmDialog>
          )}
        </Stack>
      </Card>
    </Stack>
  );
};
